(() => {
  if (typeof customElements === 'undefined' || customElements.get('hh-required-products')) return;
  const shown = new Set();
  const offerFor = card => card.querySelector('[data-rel-offer]:checked, input[type="hidden"][data-rel-offer]');
  const quantityFor = item => {
    const value = Number(item.querySelector('[data-required-quantity]')?.value);
    return Number.isInteger(value) && value >= 1 && value <= 999 ? value : null;
  };
  // Bound reads and writes, but never automatically retry a cart mutation.
  const request = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      const payload = options.html ? await response.text() : await response.json();
      if (!response.ok || payload?.status >= 400) throw new Error('Request failed');
      return payload;
    } finally { clearTimeout(timeout); }
  };
  class RequiredProducts extends HTMLElement {
    connectedCallback() {
      if (this.listeners || !this.querySelector('dialog')?.showModal) return;
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('[data-required-content]');
      this.message = this.querySelector('[data-required-message]');
      this.submit = this.querySelector('[data-required-submit]');
      this.total = this.querySelector('[data-required-total]');
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.dialog.addEventListener('click', event => {
        if (event.target.closest('[data-required-close]')) { if (!this.busy) this.dialog.close(); return; }
        const toggle = event.target.closest('[data-rel-select]');
        if (toggle && !toggle.disabled) {
          const id = offerFor(toggle.closest('[data-relationship-card]')).value;
          this.selected.has(id) ? this.selected.delete(id) : this.selected.add(id);
          this.sync();
        }
        if (event.target.closest('[data-required-submit]')) this.addSelected();
      }, options);
      this.dialog.addEventListener('change', event => {
        if (!event.target.matches('input[type="radio"][data-rel-offer]') || this.busy) return;
        const card = event.target.closest('[data-relationship-card]');
        const wasSelected = this.selected.delete(card.dataset.currentVariant);
        const offer = offerFor(card);
        if (wasSelected && offer.dataset.available === 'true') this.selected.add(offer.value);
        const picker = card.querySelector('[data-rel-picker]');
        if (picker) { picker.open = false; picker.querySelector('summary').focus(); }
        this.sync();
      }, options);
      this.dialog.addEventListener('input', event => {
        if (event.target.matches('[data-required-quantity]')) this.sync();
      }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key === 'Tab') {
          const focusable = [...this.dialog.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled):not([type="hidden"]), summary')]
            .filter(node => node.getClientRects().length && !node.closest('[hidden]') && (node.type !== 'radio' || node.checked));
          const index = focusable.indexOf(document.activeElement);
          event.preventDefault();
          focusable[(index + (event.shiftKey ? -1 : 1) + focusable.length) % focusable.length]?.focus();
        }
        if (event.key !== 'Escape') return;
        // Do not let the global drawer Escape handler steal focus from this modal.
        event.stopPropagation();
        const picker = event.target.closest('[data-rel-picker]');
        if (picker?.open) { event.preventDefault(); picker.open = false; picker.querySelector('summary').focus(); }
      }, options);
      this.dialog.addEventListener('cancel', event => { if (this.busy) event.preventDefault(); }, options);
      this.dialog.addEventListener('close', () => this.finish(), options);
    }
    disconnectedCallback() {
      this.detached = true;
      this.dialog?.close();
      document.documentElement.classList.remove('hh-required-open');
      this.listeners?.abort(); this.listeners = null;
    }
    key(variantId) { return `hh-required:${window.Shopify?.theme?.id || 'theme'}:${variantId}`; }
    hasShown(key) {
      if (shown.has(key)) return true;
      try { return sessionStorage.getItem(key) === 'shown'; } catch (_) { return false; }
    }
    remember(key) {
      shown.add(key);
      try { sessionStorage.setItem(key, 'shown'); } catch (_) { /* In-memory fallback for blocked storage. */ }
    }
    async templateFor(variantId) {
      let template = this.querySelector('[data-required-data]');
      if (template?.dataset.variantId === String(variantId)) return template;
      const url = new URL(this.dataset.sourceUrl, location.href);
      if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) throw new Error('Invalid source product');
      url.search = ''; url.hash = '';
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const html = await request(url.href, { html: true });
      template = new DOMParser().parseFromString(html, 'text/html').querySelector('hh-required-products [data-required-data]');
      if (template?.dataset.variantId !== String(variantId)) throw new Error('Required items did not match');
      return template;
    }
    async readCart() {
      const cart = await request(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
      return cart;
    }
    async afterAdd(variantId, payload, trigger, addedQuantity = 1) {
      if (!this.dialog || !this.isConnected || this.dialog.open) return false;
      const key = this.key(variantId);
      let cartRequest;
      if (this.dataset.once !== 'false' && this.hasShown(key)) {
        cartRequest = this.readCart();
        try {
          const cart = await cartRequest;
          const quantity = cart.items.filter(item => String(item.variant_id) === String(variantId))
            .reduce((total, item) => total + Number(item.quantity), 0);
          // This runs after the add: only a larger total proves the item was already in the cart.
          // A removed/re-added item must get its reminder again, even after navigating away.
          if (quantity > Number(addedQuantity)) return false;
        } catch (_) { /* An unverified cart must not silently suppress the reminder. */ }
      }
      let template;
      try { template = await this.templateFor(variantId); }
      catch (_) { announce(this.dataset.loadError); showStorefrontToast(this.dataset.loadError); return false; }
      if (!this.isConnected || !template.content.querySelector('[data-required-item]')) return false;
      this.content.replaceChildren(template.content.cloneNode(true));
      this.items = [...this.content.querySelectorAll('[data-required-item]')];
      this.items.forEach(item => {
        const quantity = item.querySelector('.hhrq__quantity');
        if (quantity) item.querySelector('.hhr-card__bottom').insertBefore(quantity, item.querySelector('[data-rel-select]'));
      });
      this.selected = new Set(); this.inCart = new Set(); this.cartReady = false; this.blocked = false; this.busy = false;
      this.detached = false; this.trigger = trigger; this.payload = payload;
      this.success = false;
      closeSurfaces();
      updateRenderedCart(payload.sections?.['cart-drawer']);
      this.sync();
      this.notice(this.dataset.checkingLabel);
      this.dialog.showModal();
      this.dialog.scrollTop = 0;
      document.documentElement.classList.add('hh-required-open');
      this.remember(key);
      announce(this.querySelector('.hhrq__confirmation').textContent.trim());
      const opening = this.opening = (this.opening || 0) + 1;
      this.checkCart(opening, cartRequest || this.readCart());
      return true;
    }
    async checkCart(opening, cartRequest) {
      try {
        const cart = await cartRequest;
        if (opening !== this.opening || !this.dialog.open) return;
        this.inCart = new Set(cart.items.map(item => String(item.variant_id)));
        this.cartReady = true;
        this.notice('');
      } catch (_) {
        if (opening !== this.opening || !this.dialog.open) return;
        this.blocked = true;
        this.notice(this.dataset.errorLabel);
      }
      this.sync();
    }
    notice(text) { this.message.textContent = text; this.message.hidden = !text; }
    choices() {
      return new Map(this.items.filter(item => item.dataset.directAdd === 'true').map(item => [offerFor(item)?.value, item])
        .filter(([id, item]) => id && this.selected.has(id) && !this.inCart.has(id) && offerFor(item).dataset.available === 'true'));
    }
    sync() {
      const money = price => formatThemeMoney(Number(price), this.dataset.currency);
      for (const item of this.items) {
        const card = item.querySelector('[data-relationship-card]');
        const offer = offerFor(card);
        if (!offer) continue;
        const direct = item.dataset.directAdd === 'true';
        const inCart = this.inCart.has(offer.value);
        const available = offer.dataset.available === 'true';
        if (!direct || inCart || !available) this.selected.delete(offer.value);
        const included = this.selected.has(offer.value);
        const toggle = card.querySelector('[data-rel-select]');
        toggle.hidden = !direct;
        toggle.disabled = this.busy || this.blocked || !this.cartReady || inCart || !available;
        toggle.setAttribute('aria-pressed', String(included));
        const label = inCart ? this.dataset.inCartLabel : !available ? this.dataset.unavailableLabel : included ? this.dataset.removeLabel : this.dataset.selectLabel;
        card.querySelector('[data-rel-select-label]').textContent = label;
        toggle.setAttribute('aria-label', `${label} - ${card.querySelector('.hhr-card__title').textContent.trim()} - ${offer.dataset.sku || offer.dataset.title}`);
        card.querySelector('[data-rel-plus]').hidden = included || inCart || !available;
        card.querySelector('[data-rel-minus]').hidden = !included;
        card.classList.toggle('is-selected', included);
        const fallback = card.querySelector('.hhr-card__fallback');
        fallback.hidden = direct;
        fallback.textContent = this.dataset.viewLabel;
        card.dataset.currentVariant = offer.value;
        card.querySelector('[data-rel-price]').textContent = money(offer.dataset.price);
        card.querySelector('[data-rel-stock]').dataset.state = offer.dataset.stock;
        card.querySelector('[data-rel-stock-label]').textContent = offer.dataset.stockLabel;
        const sku = card.querySelector('[data-rel-sku]');
        sku.textContent = offer.dataset.sku || ''; sku.hidden = !offer.dataset.sku;
        card.querySelectorAll('[data-rel-link]').forEach(link => { link.href = offer.dataset.url; });
        const choice = card.querySelector('[data-rel-choice]');
        if (choice) choice.textContent = offer.dataset.title;
        card.querySelectorAll('input[type="radio"]').forEach(input => { input.disabled = this.busy || this.blocked; });
        const img = card.querySelector('.hhr-card__photo img');
        if (img && offer.dataset.image) { img.removeAttribute('srcset'); img.src = offer.dataset.image; img.alt = offer.dataset.imageAlt; }
        const quantity = item.querySelector('[data-required-quantity]');
        if (quantity) { quantity.disabled = this.busy || this.blocked || !this.cartReady || inCart || !available; quantity.setAttribute('aria-invalid', String(quantityFor(item) === null)); }
      }
      const choices = [...this.choices().values()];
      const valid = choices.every(item => quantityFor(item) !== null);
      this.total.textContent = money(choices.reduce((sum, item) => sum + Number(offerFor(item).dataset.price) * (quantityFor(item) || 0), 0));
      this.submit.disabled = this.busy || this.blocked || !this.cartReady || !choices.length || !valid;
      this.submit.setAttribute('aria-busy', String(this.busy));
      this.querySelectorAll('[data-required-close]').forEach(button => { button.disabled = this.busy; });
    }
    async addSelected() {
      if (this.submit.disabled || this.busy) return;
      const items = [...this.choices()].map(([id, item]) => ({ id: Number(id), quantity: quantityFor(item) }));
      if (!items.length || items.some(item => !item.quantity)) return;
      this.busy = true; this.sync(); this.notice(this.dataset.addingLabel);
      try {
        const payload = await request(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: ['cart-drawer'] })
        });
        this.payload = payload;
        this.success = true;
        this.selected.clear();
      } catch (_) {
        // The server may have added some/all items even if the response was lost.
        // Require a cart review instead of exposing a duplicate-prone retry button.
        this.payload = {}; this.blocked = true;
        this.notice(this.dataset.errorLabel);
        announce(this.dataset.errorLabel);
      } finally {
        this.busy = false;
        if (this.isConnected) {
          this.sync();
          if (this.success) this.dialog.close();
        }
      }
    }
    finish() {
      this.opening = (this.opening || 0) + 1;
      document.documentElement.classList.remove('hh-required-open');
      if (this.detached || !this.trigger) return;
      const trigger = this.trigger; this.trigger = null;
      // Only reads/renders the cart here. The main SKU is never submitted again.
      showUpdatedCart(this.payload || {}, trigger);
      if (this.success) announce(this.dataset.successLabel);
    }
  }
  customElements.define('hh-required-products', RequiredProducts);
})();
